/**
 * OfflineBanner — shared connectivity warning.
 *
 * Displays a non-intrusive warning when the backend connection is lost.
 *
 * Usage:
 *   // Top-of-panel banner
 *   <OfflineBanner variant="banner" isOnline={isOnline} />
 *
 *   // Page-level adapter for PageStatusBanner. Prefer creating a
 *   // PageStatusNotice directly when composing several notices in a stack.
 *   <OfflineBanner variant="page" isOnline={isOnline} />
 *
 *   // Inline in a section (no horizontal margin)
 *   <OfflineBanner variant="warning" isOnline={isOnline} />
 *
 *   // Short note beside a heading (no margin, capped width)
 *   <OfflineBanner variant="compact" isOnline={isOnline} />
 *
 * Sidebar-local status uses this banner inside a positioned sidebar overlay.
 *   <OfflineBanner variant="sidebar" isOnline={isOnline} />
 */
import { useI18n } from '@affine/i18n';
import { STREAMLAIN_COMPACT_HEADER_HEIGHT_PX } from '@blocksuite/affine-shared/styles';
import { cssVarV2 } from '@streamlain/theme/v2';
import type { CSSProperties } from 'react';

import { PageStatusBanner } from '../page-status-banner';
import { createOfflineStatusNotice } from '../page-status-notice';

export interface OfflineBannerProps {
  /**
   * 'banner' — top-of-panel placement with horizontal margin.
   * 'page' — page-level overlay, matching normal document top banners.
   * 'warning' — inline within a section, no horizontal margin.
   * 'sidebar' — full-width strip inside a positioned sidebar overlay.
   * 'compact' — short note beside a heading, no margin, capped width.
   */
  variant: 'banner' | 'compact' | 'page' | 'sidebar' | 'warning';
  /** Current online state from the relevant connectivity source. */
  isOnline: boolean;
  /** Optional style override for custom positioning. */
  style?: CSSProperties;
  /** Optional text override for scoped offline states. */
  label?: string;
}

const baseStyle: CSSProperties = {
  padding: '8px 12px',
  borderRadius: '8px',
  fontSize: '12px',
  color: cssVarV2('text/secondary'),
  background: `color-mix(in srgb, ${cssVarV2('status/error')} 8%, transparent)`,
  border: `1px solid color-mix(in srgb, ${cssVarV2('status/error')} 30%, transparent)`,
};

const bannerStyle: CSSProperties = {
  ...baseStyle,
  margin: '0 12px 8px',
};

const warningStyle: CSSProperties = {
  ...baseStyle,
  marginBottom: '8px',
};

const compactStyle: CSSProperties = {
  ...baseStyle,
  margin: 0,
  maxWidth: '280px',
  padding: '4px 8px',
};

const sidebarStyle: CSSProperties = {
  ...baseStyle,
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
  width: '100%',
  minHeight: STREAMLAIN_COMPACT_HEADER_HEIGHT_PX,
  margin: 0,
  padding: '8px 16px',
  borderRadius: 0,
  color: cssVarV2('status/error'),
  background: `color-mix(in srgb, ${cssVarV2('status/error')} 12%, ${cssVarV2('layer/background/secondary')})`,
  border: 'none',
  borderTop: `1px solid color-mix(in srgb, ${cssVarV2('status/error')} 30%, transparent)`,
  borderBottom: `1px solid color-mix(in srgb, ${cssVarV2('status/error')} 30%, transparent)`,
};

export function OfflineBanner({
  variant,
  isOnline,
  style,
  label,
}: OfflineBannerProps) {
  const t = useI18n();

  if (isOnline) return null;

  const defaultText =
    variant === 'banner' || variant === 'page' || variant === 'sidebar'
      ? t['com.streamlain.streamlain.connectivity.offline-banner']()
      : t['com.streamlain.streamlain.connectivity.offline-warning']();
  const text = label ?? defaultText;

  if (variant === 'page') {
    return (
      <PageStatusBanner
        notice={createOfflineStatusNotice(text, {
          id: 'workflow-connectivity-offline',
        })}
        style={style}
      />
    );
  }

  const mergedStyle: CSSProperties = {
    ...(variant === 'banner'
      ? bannerStyle
      : variant === 'sidebar'
        ? sidebarStyle
        : variant === 'compact'
          ? compactStyle
          : warningStyle),
    ...style,
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-offline-banner-variant={variant}
      style={mergedStyle}
    >
      {text}
    </div>
  );
}
